export type AccessTokenGetter = () => Promise<string | null>;

interface AccessTokenRetryOptions {
  maxWaitMs?: number;
  intervalMs?: number;
  attemptTimeoutMs?: number;
  onPendingChange?: (pending: boolean) => void;
}

type TimedAttemptResult =
  | { kind: "token"; token: string }
  | { kind: "empty" }
  | { kind: "timeout" };

function parseStoredAccessToken(rawValue: string): string | null {
  const trimmed = rawValue.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as {
        accessToken?: unknown;
        token?: unknown;
      };
      if (typeof parsed.accessToken === "string" && parsed.accessToken) {
        return parsed.accessToken;
      }
      if (typeof parsed.token === "string" && parsed.token) {
        return parsed.token;
      }
      return null;
    } catch {
      return null;
    }
  }

  if (trimmed.startsWith('"')) {
    try {
      const parsed = JSON.parse(trimmed);
      return typeof parsed === "string" && parsed ? parsed : null;
    } catch {
      return null;
    }
  }

  return trimmed;
}

function readStoredAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem("workos:access-token");
    return raw ? parseStoredAccessToken(raw) : null;
  } catch {
    return null;
  }
}

async function runTokenAttempt(
  getAccessToken: AccessTokenGetter,
  attemptTimeoutMs: number,
): Promise<TimedAttemptResult> {
  if (attemptTimeoutMs <= 0) {
    const token = await getAccessToken();
    return token ? { kind: "token", token } : { kind: "empty" };
  }

  const tokenPromise: Promise<string | null> = getAccessToken().catch(
    () => null,
  );

  const timeoutSentinel = Symbol("token-timeout");
  const timedResult = await Promise.race<symbol | string | null>([
    tokenPromise,
    new Promise<symbol>((resolve) => {
      globalThis.setTimeout(() => resolve(timeoutSentinel), attemptTimeoutMs);
    }),
  ]);

  if (timedResult === timeoutSentinel) {
    return { kind: "timeout" };
  }

  return typeof timedResult === "string" && timedResult.length > 0
    ? { kind: "token", token: timedResult }
    : { kind: "empty" };
}

export async function getAccessTokenWithRetry(
  getAccessToken: AccessTokenGetter,
  options: AccessTokenRetryOptions = {},
): Promise<string | null> {
  const maxWaitMs = options.maxWaitMs ?? 5000;
  const intervalMs = Math.max(0, options.intervalMs ?? 200);
  const attemptTimeoutMs = Math.max(0, options.attemptTimeoutMs ?? 1500);

  options.onPendingChange?.(true);
  try {
    const startedAt = Date.now();
    while (Date.now() - startedAt < maxWaitMs) {
      try {
        const attempt = await runTokenAttempt(getAccessToken, attemptTimeoutMs);
        if (attempt.kind === "token") {
          return attempt.token;
        }
        const storedToken = readStoredAccessToken();
        if (storedToken) {
          return storedToken;
        }
        if (attempt.kind === "timeout") {
          continue;
        }
      } catch {
        // Ignore transient auth failures and retry.
      }

      if (intervalMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
      } else {
        await Promise.resolve();
      }
    }

    return null;
  } finally {
    options.onPendingChange?.(false);
  }
}
